'use client';

import React, { useEffect, useState } from 'react';

import { ANALYSIS_COOKIES_PROPERTY_NAME, COOKIE_CONSENT_CHANGED_EVENT } from '../constants/cookie-constants';
import { cookieStorage } from '../helpers/cookie-helper';

import FacebookPixel from './FacebookPixel';
import GoogleAnalytics from './GoogleAnalytics';
import GoogleTagManager from './GoogleTagManager';
import RedditPixel from './RedditPixel';
import TikTokPixel from './TikTokPixel';

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
        fbq?: (...args: unknown[]) => void;
        rdt?: (...args: unknown[]) => void;
        ttq?: {
            track: (event: string, data?: Record<string, unknown>) => void;
            grantConsent: () => void;
            revokeConsent: () => void;
        };
    }
}

type ConsentGatedTrackersProps = {
    gTagId?: string | string[];
    // GTM container ID (`GTM-XXXXXXX`). Independent of `gTagId`: a distributor can run only a
    // container, only their own GA4 property, or both.
    gtmContainerId?: string;
    facebookPixelId?: string;
    tiktokPixelId?: string;
    redditPixelId?: string;
};

const CONSENT_MODE_FLAGS = ['ad_storage', 'ad_user_data', 'ad_personalization', 'analytics_storage'] as const;

function consentModeUpdate(granted: boolean) {
    return Object.fromEntries(CONSENT_MODE_FLAGS.map((flag) => [flag, granted ? 'granted' : 'denied']));
}

// Loads GA4 / GTM / Meta / TikTok / Reddit only after the visitor explicitly opted into
// analysis cookies (Google "basic" Consent Mode v2: no tracker script runs before consent).
// Reacts to consent changes from the cookie banner without a reload — revoking consent tells
// the already-loaded scripts to stop processing data.
function ConsentGatedTrackers({
    gTagId,
    gtmContainerId,
    facebookPixelId,
    tiktokPixelId,
    redditPixelId
}: ConsentGatedTrackersProps): React.JSX.Element | null {
    const [consentGranted, setConsentGranted] = useState<boolean>(false);

    useEffect(() => {
        function syncConsent() {
            const granted = cookieStorage.getItem(ANALYSIS_COOKIES_PROPERTY_NAME) === true;
            setConsentGranted((wasGranted) => {
                if (wasGranted !== granted) {
                    // The next/script tags stay in the DOM once loaded, so on re-grant the inline
                    // init snippet does not re-run — signal the change to the live scripts directly.
                    // The gtag consent signal covers both gtag.js and GTM: each loader installs the
                    // same shim, and both read consent state off the shared dataLayer.
                    window.gtag?.('consent', 'update', consentModeUpdate(granted));
                    window.fbq?.('consent', granted ? 'grant' : 'revoke');
                    if (granted) {
                        window.ttq?.grantConsent();
                    } else {
                        window.ttq?.revokeConsent();
                    }
                }
                return granted;
            });
        }
        syncConsent();
        window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, syncConsent);
        return () => window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, syncConsent);
    }, []);

    if (!consentGranted) {
        return null;
    }

    return (
        <>
            {facebookPixelId && <FacebookPixel trackingId={facebookPixelId} />}
            {tiktokPixelId && <TikTokPixel trackingId={tiktokPixelId} />}
            {redditPixelId && <RedditPixel trackingId={redditPixelId} />}
            {gTagId && (
                <GoogleAnalytics
                    trackingId={gTagId}
                    consentMode
                />
            )}
            {gtmContainerId && (
                <GoogleTagManager
                    containerId={gtmContainerId}
                    consentMode
                />
            )}
        </>
    );
}

export default ConsentGatedTrackers;
